import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.otp_required) {
      return { otpRequired: true, otpToken: data.otp_token, emailHint: data.email_hint };
    }
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return { user: data.user };
  };

  const verifyOtp = async (otpToken, code) => {
    const { data } = await api.post("/auth/verify-otp", { otp_token: otpToken, code });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const resendOtp = async (otpToken) => {
    const { data } = await api.post("/auth/resend-otp", { otp_token: otpToken });
    return data.otp_token;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const googleSession = async (session_id) => {
    const { data } = await api.post("/auth/google/session", { session_id });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {/* ignore */}
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyOtp, resendOtp, register, logout, googleSession, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
